import { useState } from 'react';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('sending');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6ajcplbiueuoqm1shpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="py-28 bg-[#2C3E50]">
      <div className="max-w-5xl mx-auto px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-[1px] bg-[#D98C74]"></span>
              <span className="text-[#D98C74] text-[12px] font-semibold tracking-[0.2em] uppercase">
                Let's Talk
              </span>
            </div>
            <h2
              className="text-[30px] lg:text-[38px] font-bold text-white leading-[1.15] mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Protect Your Brand Where It Matters Most
            </h2>
            <p className="text-white/60 text-[16px] leading-[1.7] mb-10">
              No pressure. No sales pitch. Just a conversation about your supply chain and how I can
              help protect your brand.
            </p>

            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white/8 rounded-lg flex items-center justify-center">
                  <i className="ri-whatsapp-line text-[#D98C74]"></i>
                </div>
                <div>
                  <div className="text-white/40 text-[12px] uppercase tracking-wide">WhatsApp</div>
                  <span className="text-white text-[14px]">Available after onboarding</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white/8 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-2-line text-[#D98C74]"></i>
                </div>
                <div>
                  <div className="text-white/40 text-[12px] uppercase tracking-wide">Location</div>
                  <span className="text-white text-[14px]">Shanghai, China</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#F5F0E6] rounded-lg p-8">
            {formStatus === 'success' ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 bg-[#D98C74]/15 rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-[#D98C74] text-2xl"></i>
                </div>
                <h3 className="text-[22px] font-bold text-[#2C3E50] mb-2">Message Sent</h3>
                <p className="text-[#2C3E50]/55 text-[14px]">
                  I'll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form
                className="space-y-5"
                data-readdy-form
                id="brand-guardian-contact"
                onSubmit={handleSubmit}
              >
                <div>
                  <label className="block text-[12px] font-semibold text-[#2C3E50]/50 uppercase tracking-wide mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Erik Johansen"
                    required
                    className="w-full px-4 py-3.5 border border-[#D98C74]/25 rounded-md text-[14px] text-[#2C3E50] bg-white focus:outline-none focus:border-[#D98C74] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#2C3E50]/50 uppercase tracking-wide mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="erik@company.com"
                    required
                    className="w-full px-4 py-3.5 border border-[#D98C74]/25 rounded-md text-[14px] text-[#2C3E50] bg-white focus:outline-none focus:border-[#D98C74] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#2C3E50]/50 uppercase tracking-wide mb-2">
                    Company / Brand
                  </label>
                  <input
                    type="text"
                    name="company"
                    placeholder="Nordic Apparel AS"
                    required
                    className="w-full px-4 py-3.5 border border-[#D98C74]/25 rounded-md text-[14px] text-[#2C3E50] bg-white focus:outline-none focus:border-[#D98C74] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#2C3E50]/50 uppercase tracking-wide mb-2">
                    Tell Me About Your Supply Chain
                  </label>
                  <textarea
                    name="message"
                    placeholder="How many factories do you work with? What are your main concerns?"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-4 py-3.5 border border-[#D98C74]/25 rounded-md text-[14px] text-[#2C3E50] bg-white focus:outline-none focus:border-[#D98C74] transition-colors resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full bg-[#D98C74] text-white py-4 rounded-md text-[15px] font-semibold hover:bg-[#c47a62] transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 cursor-pointer"
                >
                  {formStatus === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-arrow-right-line"></i>
                    </>
                  )}
                </button>
                {formStatus === 'error' && (
                  <p className="text-red-500 text-center text-[13px]">
                    Something went wrong. Please try again or email lisa@brandguardian.no directly.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
